import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ALL_CONFIGS } from '../gql/queries/appConfig';

/**
 * Feature flags del tenant — TODOS con una sola petición.
 *
 * ── Por qué el mapa agregado y no una query por clave ──────────────────────
 *
 * Hasta 2026-08-05 cada `useFeatureFlag(key)` disparaba su propia
 * `GetConfig(key)`: una entrada de caché INDEPENDIENTE por flag, que además
 * podían desincronizarse ENTRE ELLAS. Ahora todos leen del mapa que devuelve
 * `GetAllConfigs` —una query que ya estaba escrita en este mismo fichero y
 * que no usaba nadie—. La firma pública no cambió, así que los ~40 puntos de
 * uso siguen exactamente igual.
 *
 * ── Por qué `cache-and-network` y no `cache-first` ─────────────────────────
 *
 * `cache-first` NO CADUCA: si la clave está en caché, Apollo no vuelve a
 * preguntar jamás. No hay TTL de ningún tipo (el comentario que vivía aquí
 * decía «cachear durante 5 minutos» y era sencillamente falso). Y como la
 * caché se persiste en `sessionStorage`, el valor viejo sobrevivía incluso a
 * un `Cmd+R`: un flag cambiado en Administración no llegaba NUNCA a las
 * pestañas ya abiertas — solo a una pestaña nueva, o tras «Limpiar caché».
 *
 * Con `cache-and-network` se pinta al instante lo cacheado (el arranque en
 * frío no paga nada) y se refresca por detrás.
 *
 * Efecto secundario asumido a conciencia (spec § 5): un flag que cambie a
 * mitad de sesión puede hacer aparecer o desaparecer un campo con el
 * formulario abierto — y con `showHoursType` o `directionRequired`, volver
 * obligatorio un campo que se estaba rellenando. Si algún día molesta en
 * producción, la salida es refrescar solo al cambiar de ruta, NO volver a
 * `cache-first`.
 *
 * ── La pista falsa que costó media hora ────────────────────────────────────
 *
 * La pantalla de Administración **escribe** con `SetConfigForTenant` y
 * **lee** con `AppConfigForTenant`. Son queries DISTINTAS, o sea entradas de
 * caché distintas de esta. Por eso el panel de administración podía enseñar
 * `true` mientras la aplicación se comportaba como si fuera `false`, a la vez
 * y sin mentir ninguno de los dos.
 *
 * ── Lo que NO pasa por aquí ────────────────────────────────────────────────
 *
 * `useTenantMapCenter` y `useTenantDefaultSurveillanceTeam` siguen leyendo
 * `GET_CONFIG` en crudo a propósito: sus valores NO son booleanos (un JSON de
 * coordenadas y un id de equipo) y coercionarlos a flag los rompería.
 *
 * Spec: `docs/superpowers/specs/2026-08-05-feature-flags-cache-design.md`
 */

/** Referencia estable, para no re-renderizar mientras no hay datos. */
const SIN_FLAGS: Record<string, string> = {};

interface EstadoDeFlags {
  flags: Record<string, string>;
  /**
   * `true` solo mientras NO se conoce todavía ningún valor.
   *
   * 🔴 Deliberadamente NO es el `loading` de Apollo. Con `cache-and-network`
   * ese `loading` se pone a `true` en CADA refresco de fondo, aunque ya haya
   * datos servidos desde la caché. Propagarlo tal cual haría que
   * `FeatureRoute` pintara `null` —pantalla en blanco— en mitad de una
   * navegación ya resuelta, y que `OperatorsAndVehicles` parpadeara. Lo que
   * preguntan los consumidores es «¿ya sé el valor?», no «¿hay red en
   * vuelo?».
   */
  loading: boolean;
  error: boolean;
}

/**
 * Lectura única del mapa de configuración: una query, una entrada de caché.
 * Todo lo demás de este fichero se apoya en esto.
 */
function useConfigMap(): EstadoDeFlags {
  const { data, loading, error } = useQuery(GET_ALL_CONFIGS, {
    fetchPolicy: 'cache-and-network',
    // Un flag que no exista no es un error: se cae a su valor por defecto.
    errorPolicy: 'ignore',
  });

  const mapa = data?.getAllConfigs as Record<string, string> | undefined | null;

  return {
    flags: mapa ?? SIN_FLAGS,
    loading: loading && (mapa === undefined || mapa === null),
    error: !!error,
  };
}

/** Traduce el valor crudo del servidor a booleano, con su valor por defecto. */
function aBooleano(
  valor: string | undefined | null,
  valorPorDefecto: boolean
): boolean {
  if (valor === undefined || valor === null) return valorPorDefecto;
  const v = String(valor).toLowerCase();
  return v === 'true' || v === '1' || v === 'yes';
}

/**
 * Hook para verificar si una feature está habilitada para el tenant actual
 *
 * El tenantId se obtiene automáticamente del usuario autenticado (JWT)
 * que está en el contexto de Apollo Client.
 *
 * @param featureKey - Clave del feature flag (ej: 'showMargin')
 * @param defaultValue - Valor por defecto si no existe el flag (default: false)
 * @returns true si la feature está habilitada, false si no
 *
 * @example
 * ```tsx
 * const showMargin = useFeatureFlag('showMargin', true);
 *
 * if (showMargin) {
 *   return <MarginField />;
 * }
 * return null;
 * ```
 */
export function useFeatureFlag(
  featureKey: string,
  defaultValue: boolean = false
): boolean {
  const { flags } = useConfigMap();

  // Mientras no hay mapa, `flags` está vacío → la clave sale `undefined` → se
  // devuelve `defaultValue`. Es lo mismo que hacía el `if (loading) return
  // defaultValue` de la versión anterior, pero SIN volver al valor por
  // defecto en cada refresco de fondo (que es lo que haría parpadear campos).
  return aBooleano(flags[featureKey], defaultValue);
}

/**
 * Hook que expone el estado de carga del feature flag
 *
 * Útil cuando necesitas esperar a que el feature flag se cargue antes de
 * inicializar componentes (ej: formularios con validación condicional).
 *
 * BUG #20 FIX: `isEnabled` se deriva directamente de los datos, sin estado
 * intermedio, para que no haya carrera entre `loading=false` y un `useEffect`
 * — era lo que hacía que el schema de Yup se creara con el valor incorrecto.
 *
 * @param featureKey - Clave del feature flag
 * @param defaultValue - Valor por defecto si no existe el flag (default: false)
 * @returns Objeto con { isEnabled, loading, error }
 *
 * @example
 * ```tsx
 * const { isEnabled, loading } = useFeatureFlagWithLoading('showHoursType', true);
 *
 * if (loading) {
 *   return <LoadingSpinner />;
 * }
 *
 * // Ahora isEnabled tiene el valor REAL del servidor
 * return <FormWithConditionalValidation showHoursType={isEnabled} />;
 * ```
 */
export function useFeatureFlagWithLoading(
  featureKey: string,
  defaultValue: boolean = false
): { isEnabled: boolean; loading: boolean; error: boolean } {
  const { flags, loading, error } = useConfigMap();

  const isEnabled = useMemo(
    () => aBooleano(flags[featureKey], defaultValue),
    [flags, featureKey, defaultValue]
  );

  // Log warning in development if feature flag not found
  useEffect(() => {
    if (!loading && error && process.env.NODE_ENV === 'development') {
      console.warn(
        `Feature flag ${featureKey} not found, using default:`,
        defaultValue
      );
    }
  }, [loading, error, featureKey, defaultValue]);

  return { isEnabled, loading, error };
}

/**
 * Hook para obtener todas las configuraciones del tenant en crudo
 *
 * Devuelve el mapa tal y como lo sirve el backend (valores string), sin
 * coercionar a booleano. Comparte query y caché con `useFeatureFlag`, así que
 * usarlo no cuesta ninguna petición extra.
 *
 * @returns Objeto con todos los feature flags y sus valores
 *
 * @example
 * ```tsx
 * const features = useAllFeatureFlags();
 *
 * if (features['showMargin'] === 'true') {
 *   // Mostrar el margen
 * }
 * ```
 */
export function useAllFeatureFlags(): Record<string, string> {
  const { flags } = useConfigMap();
  return flags;
}

/**
 * Hook para verificar múltiples feature flags a la vez
 *
 * Combina useAllFeatureFlags con lógica de verificación.
 *
 * @param featureKeys - Array de claves de feature flags
 * @returns Objeto con cada feature flag y su estado boolean
 *
 * @example
 * ```tsx
 * const { showMargin, showHoursType } = useFeatureFlags([
 *   'showMargin',
 *   'showHoursType',
 * ]);
 * ```
 */
export function useFeatureFlags(
  featureKeys: string[]
): Record<string, boolean> {
  const allFeatures = useAllFeatureFlags();

  return featureKeys.reduce(
    (acc, key) => {
      acc[key] = aBooleano(allFeatures[key], false);
      return acc;
    },
    {} as Record<string, boolean>
  );
}

/**
 * Helper para verificar si una feature está habilitada desde un objeto de configs
 *
 * Útil cuando ya tienes todas las configs cargadas.
 *
 * @param configs - Objeto con todas las configuraciones
 * @param featureKey - Clave del feature flag
 * @param defaultValue - Valor por defecto
 * @returns true si está habilitado, false si no
 *
 * @example
 * ```tsx
 * const configs = useAllFeatureFlags();
 * const isEnabled = isFeatureEnabled(configs, 'showMargin');
 * ```
 */
export function isFeatureEnabled(
  configs: Record<string, string>,
  featureKey: string,
  defaultValue: boolean = false
): boolean {
  return aBooleano(configs[featureKey], defaultValue);
}
